import React from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { BooleanReviewedCareElement } from "@/Flowsheets/Models/CareElements/BooleanReviewedCareElement";
import { CareComponent } from "@/Flowsheets/Models/CareElements/CareComponent";
import { CareElementType } from "@/Flowsheets/Models/CareElements/CareElementType";
import { DecimalReviewedCareElement } from "@/Flowsheets/Models/CareElements/DecimalReviewedCareElement";
import CareComponentNotes from "./CareComponentParts/CareComponentNotes";
import CareElementDateReviewedCheckbox from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedCheckbox";
import CareElementDateReviewedNumber from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedNumber";
import { ReviewedCareElementContainer } from "./CareComponentParts/DateReviewedCareElementComponents/ReviewedDateContainer";
import { ICareComponentProps } from "./Types";

import styles from "./HypertensionBasicCareComponent.module.scss";

interface HypertensionBasicCareComponent extends CareComponent {
    CareElements: Array<BooleanReviewedCareElement | DecimalReviewedCareElement>;
}

const HypertensionBasicCareComponent = React.memo(
    ({ isEditable, state, mutateState }: ICareComponentProps<HypertensionBasicCareComponent>): JSX.Element => {
        const { getText } = useTranslation();

        if (state?.CareElements == null
            || state.CareElements.length === 0
            || !state.CareElements.every(element =>
                element.CareElementType === CareElementType.DecimalReviewed || element.CareElementType === CareElementType.BooleanReviewed
            )
        ) {
            return null;
        }

        function getLabel(name: string): string {
            return getText(`Flowsheets_HypertensionBasicCareComponentLabel_${name}`);
        }

        return (
            <div>
                <div className={styles.subsectionTitleLabel}>{getText("Flowsheets_HypertensionBasicCareComponentLabel_AssessmentAndMonitoring")}</div>
                <div className={styles.dataBlockWrapper}>
                    <ReviewedCareElementContainer>
                        {state.CareElements.map(careElement => {
                            if (careElement.CareElementType === CareElementType.BooleanReviewed) {
                                return (
                                    <CareElementDateReviewedCheckbox
                                        key={`${careElement.Name}-${state.Id}`}
                                        label={getLabel(careElement.Name)}
                                        isEditable={isEditable}
                                        element={careElement}
                                        onChange={(element: BooleanReviewedCareElement) => mutateState(state => {
                                            state.CareElements = [...state.CareElements];
                                            const index = state.CareElements.findIndex(p => p.Name === element.Name);
                                            if (index !== -1) {
                                                state.CareElements[index] = element;
                                            }
                                        })}
                                    />
                                );
                            }
                            else if (careElement.CareElementType === CareElementType.DecimalReviewed) {
                                return (
                                    <CareElementDateReviewedNumber
                                        key={`${careElement.Name}-${state.Id}`}
                                        label={getLabel(careElement.Name)}
                                        isEditable={isEditable}
                                        element={careElement}
                                        onChange={(element: DecimalReviewedCareElement) => mutateState(state => {
                                            state.CareElements = [...state.CareElements];
                                            const index = state.CareElements.findIndex(p => p.Name === element.Name);
                                            if (index !== -1) {
                                                state.CareElements[index] = element;
                                            }
                                        })}
                                        type="percentage"
                                        unit="%"
                                    />
                                );
                            }
                            return null;
                        })}
                    </ReviewedCareElementContainer>
                </div>
                <CareComponentNotes
                    id="hypertensionBasicCareComponentNotes"
                    dataCy="HypertensionBasicCareComponentNotes"
                    value={state.Note}
                    isEditable={isEditable}
                    onTextChange={newValue => mutateState(s => s.Note = newValue)}
                />
            </div>
        );
    }

);

export { HypertensionBasicCareComponent };
